﻿
'use strict';

const RAMINHO_KIT_HANDLE = 'kit-degustacao-raminho';
const CART_FETCH_RETRIES = 2;
const CART_FETCH_RETRY_DELAY_MS = 250;

let siteNoticeTimer = null;

function runSoon(callback) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: 900 });
    return;
  }

  window.setTimeout(callback, 1);
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeProductGridWhitespace(root) {
  root.querySelectorAll('.product-grid').forEach((grid) => {
    [...grid.childNodes].forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
        node.remove();
      }
    });
  });
}

function showSiteNotice(message, tone = 'warning') {
  if (!message || !document.body) return;

  let notice = document.querySelector('[data-site-notice]');
  if (!notice) {
    notice = document.createElement('div');
    notice.setAttribute('data-site-notice', '');
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    document.body.appendChild(notice);
  }

  notice.className = `site-notice site-notice--${tone}`;
  notice.textContent = message;
  notice.hidden = false;

  window.requestAnimationFrame(() => notice.classList.add('is-visible'));
  window.clearTimeout(siteNoticeTimer);
  siteNoticeTimer = window.setTimeout(() => {
    notice.classList.remove('is-visible');
    window.setTimeout(() => {
      if (!notice.classList.contains('is-visible')) notice.hidden = true;
    }, 220);
  }, 4200);
}

document.addEventListener('DOMContentLoaded', () => {
  normalizeProductGridWhitespace(document);
  initCartDrawer();

  if (document.querySelector('[data-product-form]')) {
    initProductForms(document);
  }

  if (document.querySelector('[data-card-form]')) {
    runSoon(() => initDeferredProductCardForms(document));
  }

  if (document.querySelector('[data-gallery]')) {
    initMediaGallery(document);
    initGalleryLightbox(document);
    initGallerySwipe(document);
  }

  if (document.querySelector('[data-product-sticky-bar]')) {
    runSoon(initProductStickyBar);
  }

  if (document.querySelector('[data-cart-item-remove]')) {
    runSoon(initCartItemRemove);
  }

  if (document.querySelector('[data-search-form]')) {
    initSearchValidation(document);
  }

  if (document.querySelector('[data-announcement-rotator]')) {
    runSoon(initAnnouncementRotator);
  }

  if (document.querySelector('.qty-picker:not([data-qty-mode="variant"])')) {
    initQuantityPickers(document);
  }

  if (document.querySelector('[data-site-header]')) {
    window.requestAnimationFrame(initStickyHeader);
  }

  if (document.querySelector('[data-hamburger]')) {
    runSoon(initMobileNav);
  }

  if (document.querySelector('.reveal')) {
    runSoon(initReveal);
  }

  if (document.querySelector('[data-sort-select]')) {
    runSoon(initSortSelect);
  }

  if (document.querySelector('a[href*="wa.me/"], a[href*="api.whatsapp.com/send"]')) {
    runSoon(() => normalizeWhatsAppLinks(document));
  }

  if (document.querySelector('[data-cart-page-summary]')) {
    runSoon(initCartPageCheckout);
  }

  runSoon(initPreventDoubleTapZoom);
  runSoon(openCartFromUrl);
});

function initPreventDoubleTapZoom() {
  const supportsTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
  if (!supportsTouch || !isCoarsePointer) return;

  let lastTouchEnd = 0;

  document.addEventListener('touchend', (event) => {
    if (event.touches.length > 0) return;

    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
      lastTouchEnd = Date.now();
      return;
    }

    const now = Date.now();
    if (now - lastTouchEnd <= 320) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  }, { passive: false });
}

function initStickyHeader() {
  const header = document.querySelector('[data-site-header]');
  if (!header) return;
  const sync = () => header.classList.toggle('is-scrolled', window.scrollY > 16);
  sync();
  window.addEventListener('scroll', sync, { passive: true });
}

function initMobileNav() {
  const hamburger  = document.querySelector('[data-hamburger]');
  const mobileNav  = document.querySelector('[data-mobile-nav]');
  const overlay    = document.querySelector('[data-overlay]');
  if (!hamburger || !mobileNav) return;
  const firstLink = mobileNav.querySelector('a, button, [tabindex]:not([tabindex="-1"])');

  const open  = () => {
    hamburger.classList.add('is-open');
    mobileNav.classList.add('is-open');
    mobileNav.removeAttribute('inert');
    mobileNav.setAttribute('aria-hidden', 'false');
    overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    hamburger.setAttribute('aria-expanded', 'true');
    hamburger.setAttribute('aria-label', 'Fechar menu');
    window.requestAnimationFrame(() => firstLink?.focus());
  };
  const close = () => {
    hamburger.classList.remove('is-open');
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('inert', '');
    mobileNav.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.setAttribute('aria-label', 'Abrir menu');
    if (mobileNav.contains(document.activeElement)) hamburger.focus();
  };

  hamburger.addEventListener('click', () =>
    hamburger.classList.contains('is-open') ? close() : open()
  );
  overlay?.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && hamburger.classList.contains('is-open')) close();
  });

  // Close nav on link click
  mobileNav.querySelectorAll('a').forEach(a => a.addEventListener('click', close));
}

function normalizeWhatsAppLinks(root) {
  const links = root.querySelectorAll('a[href*="wa.me/"], a[href*="api.whatsapp.com/send"]');
  if (!links.length) return;

  links.forEach((link) => {
    const rawHref = link.getAttribute('href');
    if (!rawHref) return;

    let parsed;
    try {
      parsed = new URL(rawHref, window.location.origin);
    } catch {
      return;
    }

    let phone = '';
    let text = parsed.searchParams.get('text') || '';

    if (parsed.hostname.includes('wa.me')) {
      phone = parsed.pathname.replace(/\//g, '').trim();
    } else if (parsed.hostname.includes('api.whatsapp.com')) {
      phone = parsed.searchParams.get('phone') || '';
    }

    phone = phone.replace(/\D/g, '');
    if (!phone) return;

    const normalized = new URL('https://api.whatsapp.com/send');
    normalized.searchParams.set('phone', phone);
    if (text) normalized.searchParams.set('text', text);

    link.setAttribute('href', normalized.toString());
  });
}

function initReveal() {
  const items = document.querySelectorAll('.reveal');
  if (!items.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    items.forEach(el => el.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.12 });
  items.forEach(el => observer.observe(el));
}

function initQuantityPickers(root) {
  root.querySelectorAll('.qty-picker').forEach((picker) => {
    if (picker.dataset.qtyMode === 'variant') return;
    if (picker.dataset.ready === 'true') return;
    picker.dataset.ready = 'true';

    const input   = picker.querySelector('[data-qty-input]');
    const display = picker.querySelector('[data-qty-display]');
    const btnMinus = picker.querySelector('[data-qty-minus]');
    const btnPlus  = picker.querySelector('[data-qty-plus]');
    if (!input) return;

    const getMin = () => parseInt(input.min || '1', 10);
    const getVal = () => Math.max(getMin(), parseInt(input.value || '1', 10));

    const updateDisplay = () => {
      const val = getVal();
      if (display) display.textContent = val;
      if (btnMinus) btnMinus.disabled = val <= getMin();
    };

    btnMinus?.addEventListener('click', () => {
      input.value = Math.max(getMin(), getVal() - 1);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    btnPlus?.addEventListener('click', () => {
      input.value = getVal() + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    input.addEventListener('change', updateDisplay);
    updateDisplay();
  });
}

function initMediaGallery(root) {
  root.querySelectorAll('[data-gallery]').forEach((gallery) => {
    if (gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';

    const main = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    if (!main) return;

    const mediaItems = main.querySelectorAll('[data-media-id]');
    const frame = main.querySelector('.gallery-main__frame');

    // Marca o frame com a classe do tipo de mídia atualmente ativo (para CSS)
    const updateActiveType = (type) => {
      if (!frame) return;
      frame.dataset.activeType = type || 'image';
    };

    // Inicializa com o tipo da primeira mídia
    if (mediaItems.length > 0) {
      const firstActive = main.querySelector('.gallery-main__media.is-active') || mediaItems[0];
      updateActiveType(firstActive.dataset.mediaType);
    }

    // Setup de vídeos: click-to-fullscreen no mobile
    main.querySelectorAll('video[data-gallery-video]').forEach((video) => {
      video.addEventListener('click', (e) => {
        // Se o usuário tocar nos controles nativos, deixa o comportamento padrão
        // O click só dispara fullscreen se o vídeo está pausado (primeiro toque)
        if (!video.paused) return;

        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        if (!isMobile) return;

        e.preventDefault();
        // iOS Safari: usa webkitEnterFullscreen no elemento
        if (typeof video.webkitEnterFullscreen === 'function') {
          video.play().catch(() => {});
          video.webkitEnterFullscreen();
        } else if (typeof video.requestFullscreen === 'function') {
          video.requestFullscreen().then(() => video.play().catch(() => {})).catch(() => {
            video.play().catch(() => {});
          });
        } else {
          video.play().catch(() => {});
        }
      });
    });

    if (!thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const mediaId = thumb.getAttribute('data-media-id');
        if (!mediaId) return;

        // Pausa todos os vídeos ao trocar de mídia
        mediaItems.forEach((m) => {
          const video = m.querySelector('video');
          if (video) {
            video.pause();
            try { video.currentTime = 0; } catch {}
          }
          const isMatch = m.getAttribute('data-media-id') === mediaId;
          m.classList.toggle('is-active', isMatch);
          if (isMatch) {
            m.removeAttribute('aria-hidden');
            updateActiveType(m.dataset.mediaType);
          } else {
            m.setAttribute('aria-hidden', 'true');
          }
        });

        thumbs.forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  });
}

function initProductForms(root) {
  root.querySelectorAll('[data-product-form]').forEach((form) => {
    if (form.dataset.ready === 'true') return;
    form.dataset.ready = 'true';

    const section       = form.closest('[data-product-section]');
    const variantsEl    = section?.querySelector('[data-product-variants]');
    if (!section || !variantsEl) return;
    const productHandle  = section.dataset.productHandle || form.dataset.productHandle || '';

    let variants;
    try { variants = JSON.parse(variantsEl.textContent); } catch { return; }
    if (!Array.isArray(variants) || !variants.length) return;

    const variantInput        = form.querySelector('[data-variant-id]');
    const addBtn             = form.querySelector('[data-add-to-cart]');
    const priceWrap          = section.querySelector('[data-product-price]');
    const compareWrap        = section.querySelector('[data-product-compare]');
    const unitPriceEl        = section.querySelector('[data-product-unit-price]');
    const statusEl           = form.querySelector('[data-product-status]');
    const helperEl           = form.querySelector('[data-product-helper]');
    const qtyInput           = form.querySelector('[data-qty-input]');
    const optionInputs       = [...form.querySelectorAll('[data-option-input]')];
    const primaryOptionInputs = optionInputs.filter((input) => parseInt(input.getAttribute('data-option-position'), 10) === 1);
    const secondaryOptionInputs = optionInputs.filter((input) => parseInt(input.getAttribute('data-option-position'), 10) > 1);
    const variantDisplayEl   = form.querySelector('[data-product-variant-display]');
    const variantMinusBtn    = form.querySelector('[data-product-variant-minus]');
    const variantPlusBtn     = form.querySelector('[data-product-variant-plus]');
    const hasGroupedPrimarySelector = primaryOptionInputs.length > 0 && variantDisplayEl && variantMinusBtn && variantPlusBtn;

    const updateSelectedOptionLabel = (position, value) => {
      section.querySelectorAll(`[data-selected-option-${position}]`).forEach((el) => {
        el.textContent = value || '';
      });
    };

    const getSelectedOptionsByPosition = () => {
      const selected = [];
      optionInputs.forEach((input) => {
        if (!input.checked) return;
        const position = parseInt(input.getAttribute('data-option-position'), 10);
        if (!Number.isFinite(position) || position <= 0) return;
        selected[position - 1] = input.value;
      });
      return selected;
    };

    const syncPrimaryOptionValue = (value) => {
      if (!primaryOptionInputs.length || !value) return;
      const normalizedValue = normalizeLabel(value);
      primaryOptionInputs.forEach((input) => {
        input.checked = normalizeLabel(input.value) === normalizedValue;
      });
      updateSelectedOptionLabel(1, value);
    };

    const updateProductUI = ({
      label = '',
      variant = null,
      price = 0,
      compareAtPrice = 0,
      unitPriceText = '',
      helperText = '',
      quantity = 1,
      resetQuantity = true
    }) => {
      const nextQuantity = Math.max(1, parseInt(quantity, 10) || 1);
      if (variantInput) variantInput.value = variant?.id || '';
      if (qtyInput) qtyInput.value = String(resetQuantity ? 1 : nextQuantity);
      if (variantDisplayEl && label) variantDisplayEl.textContent = label;
      if (label) syncPrimaryOptionValue(label);

      if (priceWrap) priceWrap.textContent = formatMoney(price || 0);

      if (compareWrap) {
        const onSale = Number(compareAtPrice) > Number(price);
        compareWrap.hidden = !onSale;
        compareWrap.textContent = onSale ? formatMoney(compareAtPrice) : '';
      }

      if (unitPriceEl) {
        unitPriceEl.hidden = !unitPriceText;
        unitPriceEl.textContent = unitPriceText;
      }

      if (helperEl) {
        helperEl.hidden = !helperText;
        helperEl.textContent = helperText;
      }

      if (addBtn) {
        addBtn.classList.remove('is-loading', 'is-success');
        addBtn.disabled = !variant?.available;
        addBtn.textContent = variant?.available ? 'Adicionar ao carrinho' : 'Indisponível';
        addBtn._originalText = addBtn.textContent;
      }

      if (statusEl) {
        statusEl.textContent = variant?.available ? '' : 'Esta opção está esgotada.';
        statusEl.className = 'product-status' + (variant?.available ? '' : ' is-error');
      }
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const position = parseInt(input.getAttribute('data-option-position'), 10);
        if (!Number.isFinite(position) || position <= 0) return;
        updateSelectedOptionLabel(position, input.value);
      });
    });

    if (hasGroupedPrimarySelector) {
      let currentVariantGroups = [];
      let quantityExpansion = null;
      let selectedIndex = 0;
      let extraStepCount = 0;

      const findGroupIndex = (groups, label) => groups.findIndex((group) => normalizeLabel(group.label) === normalizeLabel(label));

      const syncAvailableGroups = (preferredLabel = '') => {
        const selectedOptions = getSelectedOptionsByPosition();
        const filteredVariants = variants.filter((variant) => (
          variant.options.every((optionValue, index) => {
            if (index === 0) return true;
            return !selectedOptions[index] || optionValue === selectedOptions[index];
          })
        ));

        currentVariantGroups = buildVariantGroups(filteredVariants);
        quantityExpansion = buildQuantityExpansionConfig(currentVariantGroups);

        if (!currentVariantGroups.length) {
          selectedIndex = 0;
          extraStepCount = 0;
          return;
        }

        let nextIndex = preferredLabel ? findGroupIndex(currentVariantGroups, preferredLabel) : -1;
        if (nextIndex < 0) {
          const checkedPrimaryValue = primaryOptionInputs.find((input) => input.checked)?.value;
          if (checkedPrimaryValue) nextIndex = findGroupIndex(currentVariantGroups, checkedPrimaryValue);
        }
        if (nextIndex < 0) nextIndex = currentVariantGroups.findIndex((group) => group.variant?.available);
        if (nextIndex < 0) nextIndex = 0;

        selectedIndex = nextIndex;
        extraStepCount = 0;
      };

      const getSelectionState = () => {
        const activeGroup = currentVariantGroups[selectedIndex] || currentVariantGroups[0];
        if (!activeGroup?.variant) return null;

        if (quantityExpansion && selectedIndex === currentVariantGroups.length - 1 && extraStepCount > 0) {
          const expandedSelection = buildExpandedSelectionState(
            quantityExpansion,
            extraStepCount,
            getVariantHelperText(currentVariantGroups)
          );
          if (expandedSelection) return expandedSelection;
        }

        return {
          label: activeGroup.label,
          variant: activeGroup.variant,
          price: activeGroup.variant.price,
          compareAtPrice: activeGroup.variant.compare_at_price,
          unitPriceText: getVariantUnitPriceText(activeGroup.variant, activeGroup.label),
          helperText: getVariantHelperText(currentVariantGroups),
          cartItems: [
            { id: activeGroup.variant.id, quantity: 1 }
          ]
        };
      };

      const applyGroupedSelection = () => {
        const selection = getSelectionState();
        if (!selection?.variant) {
          if (variantInput) variantInput.value = '';
          if (variantDisplayEl) variantDisplayEl.textContent = 'Indisponível';
          if (unitPriceEl) {
            unitPriceEl.hidden = true;
            unitPriceEl.textContent = '';
          }
          if (helperEl) {
            helperEl.hidden = true;
            helperEl.textContent = '';
          }
          if (addBtn) {
            addBtn.disabled = true;
            addBtn.textContent = 'Indisponível';
            addBtn._originalText = addBtn.textContent;
          }
          if (statusEl) {
            statusEl.textContent = 'Esta combinação não está disponível.';
            statusEl.className = 'product-status is-error';
          }
          if (variantMinusBtn) variantMinusBtn.disabled = true;
          if (variantPlusBtn) variantPlusBtn.disabled = true;
          return;
        }

        const { label, variant, price, compareAtPrice, unitPriceText, helperText } = selection;
        updateProductUI({
          label,
          variant,
          price,
          compareAtPrice,
          unitPriceText,
          helperText: currentVariantGroups.length > 1 ? helperText : ''
        });

        if (variantMinusBtn) variantMinusBtn.disabled = selectedIndex <= 0 && extraStepCount <= 0;
        if (variantPlusBtn) variantPlusBtn.disabled = !quantityExpansion && selectedIndex >= currentVariantGroups.length - 1;
      };

      secondaryOptionInputs.forEach((input) => {
        input.addEventListener('change', () => {
          const currentLabel = getSelectionState()?.label || currentVariantGroups[selectedIndex]?.label || '';
          syncAvailableGroups(currentLabel);
          applyGroupedSelection();
        });
      });

      variantMinusBtn?.addEventListener('click', () => {
        if (quantityExpansion && extraStepCount > 0) {
          extraStepCount -= 1;
          applyGroupedSelection();
          return;
        }
        if (selectedIndex <= 0) return;
        selectedIndex -= 1;
        extraStepCount = 0;
        applyGroupedSelection();
      });

      variantPlusBtn?.addEventListener('click', () => {
        if (quantityExpansion && selectedIndex >= currentVariantGroups.length - 1) {
          extraStepCount += 1;
          applyGroupedSelection();
          return;
        }
        if (selectedIndex >= currentVariantGroups.length - 1) return;
        selectedIndex += 1;
        extraStepCount = 0;
        applyGroupedSelection();
      });

      syncAvailableGroups(primaryOptionInputs.find((input) => input.checked)?.value || '');
      applyGroupedSelection();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const selection = getSelectionState();
        const cartItems = tagKitPayloadItems(selection?.cartItems || [], productHandle);
        if (!selection?.variant?.available || !cartItems.length) return;

        if (addBtn) setButtonLoading(addBtn, true);
        if (statusEl) {
          statusEl.textContent = '';
          statusEl.className = 'product-status';
        }

        try {
          const cart = await addToCart(cartItems.length === 1 ? cartItems[0] : cartItems);
          updateCartState(cart);
          if (addBtn) setButtonSuccess(addBtn);
          if (statusEl) {
            statusEl.textContent = 'Adicionado ao carrinho!';
            statusEl.className = 'product-status is-success';
            setTimeout(() => {
              if (statusEl) {
                statusEl.textContent = '';
                statusEl.className = 'product-status';
              }
            }, 3000);
          }
          openCartDrawer();
        } catch (err) {
          if (addBtn) setButtonLoading(addBtn, false);
          if (statusEl) {
            statusEl.textContent = err.message || 'Não foi possível adicionar. Tente novamente.';
            statusEl.className = 'product-status is-error';
          }
        }
      });

      return;
    }

    const findVariant = () => {
      if (!optionInputs.length) {
        // Sem pills nativas (ex.: Kit Degustação) — respeita o id que o snippet escreveu.
        const overrideId = variantInput?.value;
        if (overrideId) {
          const direct = variants.find((v) => String(v.id) === String(overrideId));
          if (direct) return direct;
        }
        return variants[0];
      }
      const selected = getSelectedOptionsByPosition();
      return variants.find((variant) => variant.options.every((optionValue, index) => optionValue === selected[index])) || null;
    };

    const applyVariant = (variant) => {
      if (!variant) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = 'Indisponível';
          addBtn._originalText = addBtn.textContent;
        }
        if (unitPriceEl) {
          unitPriceEl.hidden = true;
          unitPriceEl.textContent = '';
        }
        if (helperEl) {
          helperEl.hidden = true;
          helperEl.textContent = '';
        }
        return;
      }

      const label = getPrimaryVariantLabel(variant);
      updateProductUI({
        label,
        variant,
        price: variant.price,
        compareAtPrice: variant.compare_at_price,
        unitPriceText: getVariantUnitPriceText(variant, label),
        helperText: '',
        quantity: 1,
        resetQuantity: true
      });
    };

    const getSelectedQuantity = () => Math.max(1, parseInt(qtyInput?.value || '1', 10) || 1);

    const syncSimpleQuantityPricing = (variant, { resetQuantity = false } = {}) => {
      if (!variant) return;

      const label = getPrimaryVariantLabel(variant);
      const quantity = resetQuantity ? 1 : getSelectedQuantity();

      updateProductUI({
        label,
        variant,
        price: (Number(variant.price) || 0) * quantity,
        compareAtPrice: (Number(variant.compare_at_price) || 0) * quantity,
        unitPriceText: getVariantUnitPriceText(variant, label),
        helperText: '',
        quantity,
        resetQuantity
      });
    };

    optionInputs.forEach((input) => input.addEventListener('change', () => syncSimpleQuantityPricing(findVariant(), { resetQuantity: true })));
    qtyInput?.addEventListener('change', () => syncSimpleQuantityPricing(findVariant(), { resetQuantity: false }));
    syncSimpleQuantityPricing(findVariant(), { resetQuantity: true });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const variant = findVariant();
      if (!variant?.available) return;

      const qty = isKitProductHandle(productHandle) ? 1 : parseInt(qtyInput?.value || '1', 10);

      if (addBtn) setButtonLoading(addBtn, true);
      if (statusEl) {
        statusEl.textContent = '';
        statusEl.className = 'product-status';
      }

      try {
        const cart = await addToCart(tagKitPayloadItems([{ id: variant.id, quantity: qty }], productHandle)[0]);
        updateCartState(cart);
        if (addBtn) setButtonSuccess(addBtn);
        if (statusEl) {
          statusEl.textContent = 'Adicionado ao carrinho!';
          statusEl.className = 'product-status is-success';
          setTimeout(() => {
            if (statusEl) {
              statusEl.textContent = '';
              statusEl.className = 'product-status';
            }
          }, 3000);
        }
        openCartDrawer();
      } catch (err) {
        if (addBtn) setButtonLoading(addBtn, false);
        if (statusEl) {
          statusEl.textContent = err.message || 'Não foi possível adicionar. Tente novamente.';
          statusEl.className = 'product-status is-error';
        }
      }
    });
  });
}

function initProductCardForms(root) {
  root.querySelectorAll('[data-card-form]').forEach((form) => {
    if (form.dataset.ready === 'true') return;
    form.dataset.ready = 'true';

    const cardEl = form.closest('.product-card');
    const variantsEl = form.querySelector('[data-card-variants]');
    const productHandle = form.dataset.productHandle || '';
    const isFixedKitCard = form.dataset.cardFixedKit === 'true' && isKitProductHandle(productHandle);
    let variants = [];
    if (variantsEl) {
      try { variants = JSON.parse(variantsEl.textContent); } catch {}
      variantsEl.remove();
    }

    const variantInput = form.querySelector('[data-card-variant-id]');
    const qtyInput     = form.querySelector('[data-qty-input]');
    const displayEl    = form.querySelector('[data-card-variant-label]');
    const addBtn       = form.querySelector('[data-card-add]');
    const statusEl     = form.querySelector('[data-card-status]');
    const badgeEl      = cardEl?.querySelector('[data-card-variant-badge]');
    const helperEl     = form.querySelector('[data-card-helper]');
    const priceMetaEl  = cardEl?.querySelector('[data-card-price-meta]');
    const priceEl      = cardEl?.querySelector('[data-card-price-current]');
    const compareEl    = cardEl?.querySelector('[data-card-price-compare]');
    const unitPriceEl  = cardEl?.querySelector('[data-card-unit-price]');
    const btnMinus     = form.querySelector('[data-qty-minus]');
    const btnPlus      = form.querySelector('[data-qty-plus]');

    const variantGroups = buildVariantGroups(variants);
    if (!variantGroups.length) return;
    const availableKitVariants = isFixedKitCard
      ? variants.filter((variant) => variant?.available !== false)
      : [];
    let fixedKitVariant = availableKitVariants[0] || variants[0];
    const quantityExpansion = buildQuantityExpansionConfig(variantGroups);
    const simpleQuantityMode = variantGroups.length === 1;

    let selectedIndex = variantGroups.findIndex((group) => group.variant?.available);
    if (selectedIndex < 0) selectedIndex = 0;
    let extraStepCount = 0;
    let simpleQuantity = Math.max(1, parseInt(qtyInput?.value || '1', 10));

    const getSimpleQuantityLabel = () => {
      const baseLabel = variantGroups[selectedIndex]?.label || '';
      if (simpleQuantity === 1) return baseLabel || '1 un';
      const measurement = variantGroups[selectedIndex]?.measurement;
      if (measurement) {
        const baseAmount = getMeasurementBaseAmount(measurement);
        if (baseAmount > 0) {
          const total = baseAmount * simpleQuantity;
          if (measurement.kind === 'weight') return formatWeightLabelFromGrams(total);
          if (measurement.kind === 'volume') return formatVolumeLabel(total);
          if (measurement.kind === 'unit') return `${total} un`;
        }
      }
      return `${simpleQuantity} × ${baseLabel || 'un'}`;
    };

    const getSimpleQuantityHelperText = () => {
      const measurement = variantGroups[selectedIndex]?.measurement;
      if (!measurement) return 'Use + e - para ajustar a quantidade.';
      if (measurement.kind === 'weight') return 'Use + e - para ajustar o peso.';
      if (measurement.kind === 'volume') return 'Use + e - para ajustar o volume.';
      return 'Use + e - para ajustar a quantidade.';
    };

    const getSelectionState = () => {
      if (isFixedKitCard) {
        return {
          label: '1',
          variant: fixedKitVariant,
          price: fixedKitVariant?.price,
          compareAtPrice: fixedKitVariant?.compare_at_price,
          unitPriceText: '',
          helperText: '',
          cartItems: tagKitPayloadItems([
            { id: fixedKitVariant?.id, quantity: 1 }
          ], productHandle)
        };
      }

      const activeGroup = variantGroups[selectedIndex] || variantGroups[0];
      if (!activeGroup?.variant) return null;

      if (simpleQuantityMode) {
        const totalPrice = (Number(activeGroup.variant.price) || 0) * simpleQuantity;
        const totalCompareAtPrice = (Number(activeGroup.variant.compare_at_price) || 0) * simpleQuantity;

        return {
          label: getSimpleQuantityLabel(),
          variant: activeGroup.variant,
          price: totalPrice,
          compareAtPrice: totalCompareAtPrice,
          unitPriceText: getVariantUnitPriceText(activeGroup.variant, activeGroup.label),
          helperText: getSimpleQuantityHelperText(),
          cartItems: tagKitPayloadItems([
            { id: activeGroup.variant.id, quantity: simpleQuantity }
          ], productHandle)
        };
      }

      if (quantityExpansion && selectedIndex === variantGroups.length - 1 && extraStepCount > 0) {
        const expandedSelection = buildExpandedSelectionState(
          quantityExpansion,
          extraStepCount,
          getVariantHelperText(variantGroups)
        );
        if (expandedSelection) return expandedSelection;
      }

      return {
        label: activeGroup.label,
        variant: activeGroup.variant,
        price: activeGroup.variant.price,
        compareAtPrice: activeGroup.variant.compare_at_price,
        unitPriceText: getVariantUnitPriceText(activeGroup.variant, activeGroup.label),
        helperText: getVariantHelperText(variantGroups),
          cartItems: tagKitPayloadItems([
            { id: activeGroup.variant.id, quantity: 1 }
          ], productHandle)
      };
    };

    const applyVariantGroup = () => {
      const selection = getSelectionState();
      if (!selection?.variant) return;

      const { label, variant, price, compareAtPrice, unitPriceText, helperText } = selection;

      if (variantInput) variantInput.value = variant.id;
      if (qtyInput) qtyInput.value = simpleQuantityMode ? String(simpleQuantity) : '1';
      if (displayEl) displayEl.textContent = label;
      if (badgeEl) badgeEl.textContent = label;

      if (priceEl) priceEl.textContent = formatMoney(price);
      if (priceMetaEl) priceMetaEl.setAttribute('content', String((price || 0) / 100));

      if (compareEl) {
        const onSale = Number(compareAtPrice) > Number(price);
        compareEl.hidden = !onSale;
        compareEl.textContent = onSale ? formatMoney(compareAtPrice) : '';
      }

      if (unitPriceEl) {
        unitPriceEl.hidden = !unitPriceText;
        unitPriceEl.textContent = unitPriceText;
      }

      if (helperEl) {
        helperEl.hidden = !helperText || (!simpleQuantityMode && variantGroups.length <= 1);
        helperEl.textContent = helperText;
      }

      if (btnMinus) {
        btnMinus.disabled = isFixedKitCard ? true : (simpleQuantityMode
          ? simpleQuantity <= 1
          : (selectedIndex <= 0 && extraStepCount <= 0));
        btnMinus.setAttribute('aria-label', simpleQuantityMode ? 'Diminuir quantidade' : 'Selecionar medida anterior');
      }

      if (btnPlus) {
        btnPlus.disabled = isFixedKitCard ? true : (simpleQuantityMode
          ? false
          : (!quantityExpansion && selectedIndex >= variantGroups.length - 1));
        btnPlus.setAttribute('aria-label', simpleQuantityMode ? 'Aumentar quantidade' : 'Selecionar próxima medida');
      }

      if (addBtn) {
        addBtn.classList.remove('is-loading', 'is-success');
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available
          ? (addBtn.dataset.availableLabel || 'Comprar')
          : (addBtn.dataset.unavailableLabel || 'Indisponível');
        addBtn.setAttribute(
          'aria-label',
          variant.available
            ? `Comprar ${label} por ${formatMoney(price)}`
            : 'Produto indisponivel'
        );
        addBtn._originalText = addBtn.textContent;
      }

      if (statusEl) {
        statusEl.textContent = '';
        statusEl.className = 'product-card__status';
      }
    };

    btnMinus?.addEventListener('click', () => {
      if (simpleQuantityMode) {
        if (simpleQuantity <= 1) return;
        simpleQuantity -= 1;
        applyVariantGroup();
        return;
      }

      if (quantityExpansion && extraStepCount > 0) {
        extraStepCount -= 1;
        applyVariantGroup();
        return;
      }
      if (selectedIndex <= 0) return;
      selectedIndex -= 1;
      extraStepCount = 0;
      applyVariantGroup();
    });

    btnPlus?.addEventListener('click', () => {
      if (simpleQuantityMode) {
        simpleQuantity += 1;
        applyVariantGroup();
        return;
      }

      if (quantityExpansion && selectedIndex >= variantGroups.length - 1) {
        extraStepCount += 1;
        applyVariantGroup();
        return;
      }
      if (selectedIndex >= variantGroups.length - 1) return;
      selectedIndex += 1;
      extraStepCount = 0;
      applyVariantGroup();
    });

    applyVariantGroup();

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (isFixedKitCard && availableKitVariants.length) {
        fixedKitVariant = availableKitVariants[Math.floor(Math.random() * availableKitVariants.length)];
        if (variantInput) variantInput.value = fixedKitVariant.id;
      }

      const selection = getSelectionState();
      const cartItems = selection?.cartItems || [];
      if (!cartItems.length) return;

      setButtonLoading(addBtn, true);
      if (statusEl) { statusEl.textContent = ''; statusEl.className = 'product-card__status'; }

      try {
        const cart = await addToCart(cartItems.length === 1 ? cartItems[0] : cartItems);
        updateCartState(cart);
        setButtonSuccess(addBtn);
        openCartDrawer();
        if (statusEl) {
          statusEl.textContent = 'Produto adicionado ao carrinho.';
          statusEl.className = 'product-card__status is-success';
          setTimeout(() => { if (statusEl) { statusEl.textContent = ''; statusEl.className = 'product-card__status'; } }, 2500);
        }
      } catch (err) {
        setButtonLoading(addBtn, false);
        if (statusEl) {
          statusEl.textContent = err.message || 'Tente novamente.';
          statusEl.className = 'product-card__status is-error';
        }
      }
    });
  });
}

function initDeferredProductCardForms(root) {
  const forms = [...root.querySelectorAll('[data-card-form]')].filter((form) => form.dataset.deferReady !== 'true');
  if (!forms.length) return;

  if (!('IntersectionObserver' in window)) {
    initProductCardForms(root);
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const target = entry.target;
      initProductCardForms(target);
      observer.unobserve(target);
    });
  }, {
    rootMargin: '320px 0px'
  });

  forms.forEach((form) => {
    form.dataset.deferReady = 'true';
    const target = form.closest('.product-card') || form;

    const activate = () => {
      initProductCardForms(target);
      observer.unobserve(target);
    };

    target.addEventListener('pointerenter', activate, { once: true, passive: true });
    target.addEventListener('focusin', activate, { once: true });
    target.addEventListener('touchstart', activate, { once: true, passive: true });
    observer.observe(target);
  });
}

let cartState = null;

function initCartDrawer() {
  const drawer   = document.querySelector('[data-cart-drawer]');
  const body     = drawer?.querySelector('[data-cart-body]');
  if (!drawer || !body) return;

  const initialCart = parseJson(document.querySelector('[data-cart-state]')) || makeEmptyCart();
  cartState = buildCartState(drawer, body, initialCart);

  cartState.closeButtons.forEach(btn => btn.addEventListener('click', closeCartDrawer));
  drawer.addEventListener('click', onDrawerBodyClick);
  drawer.addEventListener('change', onDrawerChange);

  document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
    if (trigger.dataset.cartReady === 'true') return;
    trigger.dataset.cartReady = 'true';
    trigger.addEventListener('click', (e) => { e.preventDefault(); openCartDrawer(); });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartState?.drawer.classList.contains('is-open')) closeCartDrawer();
  });

  document.querySelector('[data-overlay]')?.addEventListener('click', () => {
    if (cartState?.drawer.classList.contains('is-open')) closeCartDrawer();
  });

  renderCartDrawer(initialCart);

  window.raminhoTheme = window.raminhoTheme || {};
  window.raminhoTheme.openCartDrawer = openCartDrawer;
}

function buildCartState(drawer, body, cart) {
  return {
    drawer,
    body,
    closeButtons: [...drawer.querySelectorAll('[data-cart-close]')],
    countEls: [...document.querySelectorAll('[data-cart-count]')],
    drawerCountEl: drawer.querySelector('[data-cart-drawer-count]'),
    subtotalEl: drawer.querySelector('[data-cart-subtotal]'),
    progressFill: drawer.querySelector('[data-progress-fill]'),
    progressText: drawer.querySelector('[data-progress-text]'),
    minimumEl: drawer.querySelector('[data-cart-minimum]'),
    footerEl: drawer.querySelector('[data-cart-footer]'),
    checkoutBtn: drawer.querySelector('[data-checkout-button]'),
    checkoutNoteEl: drawer.querySelector('[data-cart-checkout-note]'),
    cart
  };
}

function openCartDrawer() {
  if (!cartState) return;
  cartState.drawer.classList.add('is-open');
  document.querySelector('[data-overlay]')?.classList.add('is-active');
  document.body.style.overflow = 'hidden';
  cartState.drawer.setAttribute('aria-hidden', 'false');
}
function closeCartDrawer() {
  if (!cartState) return;
  cartState.drawer.classList.remove('is-open');
  document.querySelector('[data-overlay]')?.classList.remove('is-active');
  // Only restore scroll if mobile nav is also closed
  if (!document.querySelector('[data-mobile-nav]')?.classList.contains('is-open')) {
    document.body.style.overflow = '';
  }
  cartState.drawer.setAttribute('aria-hidden', 'true');
}

function openCartFromUrl() {
  if (new URLSearchParams(window.location.search).get('cart') === 'open') openCartDrawer();
}

async function startCheckoutFlow() {
  closeCartDrawer();
  // Tenta Yampi primeiro COM safety net: se o carrinho tiver produto que a Yampi
  // ainda não sincronizou (ex: produto novo), cai no checkout Shopify nativo
  // em vez de ficar travado com URL vazia.
  safeYampiOrFallback();
}

function safeYampiOrFallback() {
  const fallbackUrl = window.raminhoTheme?.routes?.checkout || '/checkout';

  // Se Yampi não carregou, vai direto pro fallback
  if (typeof window.yampiClick !== 'function') {
    window.location.href = fallbackUrl;
    return;
  }

  // Mostra loader da Yampi (função fornecida pelo YampiSnippet)
  if (typeof window.showYampiLoader === 'function') {
    try { window.showYampiLoader(); } catch (e) {}
  }

  // Refaz a chamada que yampiClick() faz, mas com guardião da URL vazia
  fetch('/cart.json', { credentials: 'same-origin' })
    .then((r) => r.json())
    .then((cartPayload) =>
      fetch('https://api.dooki.com.br/v2/public/shopify/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          shop: window.location.host,
          shopify_internal_domain: window.location.host,
          cart_payload: cartPayload,
        }),
      }).then((r) => r.json())
    )
    .then((resp) => {
      const url = (resp && resp.checkout_direct_url) || '';
      if (url && url.trim() !== '') {
        const finalUrl = typeof window.ymp_getUrlWithUtms === 'function' ? window.ymp_getUrlWithUtms(url) : url;
        window.location.href = finalUrl;
      } else {
        console.warn('[raminho] Yampi sem URL de checkout (produto provavelmente não sincronizado). Usando checkout Shopify nativo.');
        window.location.href = fallbackUrl;
      }
    })
    .catch((err) => {
      console.warn('[raminho] Falha na chamada Yampi, usando fallback:', err);
      window.location.href = fallbackUrl;
    });
}

function initCartPageCheckout() {
  const summary = document.querySelector('[data-cart-page-summary]');
  const checkoutArea = summary?.querySelector('[data-cart-page-checkout]');
  if (!summary || !checkoutArea) return;

  const minimum = parseInt(
    summary.getAttribute('data-minimum-cents')
      || window.raminhoTheme?.cartRules?.minimumCents
      || '9990',
    10
  );
  const total = parseInt(summary.getAttribute('data-total-price') || '0', 10);
  const itemCount = parseInt(summary.getAttribute('data-item-count') || '0', 10);
  const cartEmpty = itemCount === 0;

  // Sincroniza data-attributes nos botões para o validador
  const syncButtonState = (button) => {
    if (!(button instanceof HTMLElement)) return;
    button.dataset.minCents = String(minimum);
    button.dataset.cartTotalCents = String(total);
    // Carrinho vazio é o único caso que mantém botão desabilitado de fato
    if (cartEmpty) {
      button.classList.add('is-disabled');
      button.setAttribute('aria-disabled', 'true');
      if (button.tagName === 'BUTTON') button.disabled = true;
      button.setAttribute('tabindex', '-1');
    } else {
      button.classList.remove('is-disabled');
      button.setAttribute('aria-disabled', 'false');
      if (button.tagName === 'BUTTON') button.disabled = false;
      button.removeAttribute('tabindex');
    }
  };

  const syncCheckoutButtons = () => {
    checkoutArea
      .querySelectorAll('[data-cart-page-checkout-anchor], .ymp_check_mainBtn, .ymp_elem_mainBtn')
      .forEach(syncButtonState);
  };

  const routeThroughYampi = () => {
    if (typeof window.yampiClick !== 'function') return false;
    startCheckoutFlow();
    return true;
  };

  // Capture phase: precisa rodar ANTES do listener do Yampi
  checkoutArea.addEventListener('click', (event) => {
    const target = event.target instanceof Element
      ? event.target.closest('[data-cart-page-checkout-anchor], .ymp_check_mainBtn, .ymp_elem_mainBtn')
      : null;

    if (!target) return;

    // Carrinho vazio: bloqueio direto, sem modal
    if (cartEmpty) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Pedido abaixo do mínimo: mostra modal e impede checkout
    if (!validateMinimumOrShowModal(target)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Pedido OK: mostra cross-sell modal antes de prosseguir
    if (sessionStorage.getItem('raminhoCrosssellShown') !== 'true') {
      event.preventDefault();
      event.stopPropagation();
      const proceed = () => {
        if (target.matches('[data-cart-page-checkout-anchor]') && routeThroughYampi()) {
          // Yampi assume
          return;
        }
        // Fallback: navega para o href
        const href = target.getAttribute('href') || '/checkout';
        window.location.href = href;
      };
      showCrosssellModalAndProceed(proceed);
      return;
    }

    // Cross-sell já foi visto: deixa o Yampi cuidar normalmente
    if (target.matches('[data-cart-page-checkout-anchor]') && routeThroughYampi()) {
      event.preventDefault();
    }
  }, true);

  checkoutArea.addEventListener('submit', (event) => {
    if (cartEmpty) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const submitter = event.submitter || checkoutArea.querySelector('[data-cart-page-checkout-anchor]');
    if (submitter && !validateMinimumOrShowModal(submitter)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  const observer = new MutationObserver(syncCheckoutButtons);
  observer.observe(checkoutArea, { childList: true, subtree: true });

  syncCheckoutButtons();
  window.setTimeout(syncCheckoutButtons, 800);
  window.setTimeout(syncCheckoutButtons, 1800);
}

async function onDrawerBodyClick(e) {
  const checkoutButton = e.target.closest('[data-checkout-button]');
  if (checkoutButton) {
    e.preventDefault();

    // Carrinho vazio: continua bloqueado fisicamente
    if (checkoutButton.disabled || checkoutButton.classList.contains('is-disabled')) {
      return;
    }

    // Valida pedido mínimo via modal
    if (!validateMinimumOrShowModal(checkoutButton)) {
      return;
    }

    // Acima do mínimo: mostra cross-sell antes (uma vez por sessão)
    showCrosssellModalAndProceed(() => startCheckoutFlow());
    return;
  }

  const btn = e.target.closest('[data-cart-remove]');
  if (btn) {
    const key = btn.getAttribute('data-cart-remove');
    try {
      const cart = await changeCartItem(key, 0);
      updateCartState(cart);
    } catch {}
  }
}

async function onDrawerChange(e) {
  const input = e.target.closest('[data-cart-qty-input]');
  if (input) {
    const key = input.getAttribute('data-item-key');
    const qty = parseInt(input.value, 10);
    try {
      const cart = await changeCartItem(key, qty);
      updateCartState(cart);
    } catch {}
  }
}

function updateCartState(cart) {
  if (!cartState) return;
  cartState.cart = cart;
  renderCartDrawer(cart);
  updateCountBadges(cart.item_count);
}

function renderCartDrawer(cart) {
  if (!cartState) return;

  const itemCount = cart.item_count || 0;
  const total = cart.total_price || 0;
  const threshold = window.raminhoTheme?.cartRules?.freeShipCents || 49900;
  const minimum = window.raminhoTheme?.cartRules?.minimumCents || 9990;
  const remainingForShipping = Math.max(0, threshold - total);
  const remainingForMinimum = Math.max(0, minimum - total);
  const hasItems = itemCount > 0;
  const pct = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 0;

  updateCountBadges(itemCount);

  if (cartState.drawerCountEl) {
    cartState.drawerCountEl.textContent = formatCartItemCount(itemCount);
  }

  if (cartState.subtotalEl) {
    cartState.subtotalEl.textContent = formatMoney(total);
  }

  if (cartState.progressFill) {
    cartState.progressFill.style.width = `${pct}%`;
  }

  if (cartState.progressText) {
    cartState.progressText.innerHTML = hasItems && remainingForShipping === 0
      ? '<span>Frete por nossa conta liberado · entrega com seguro de frescor</span>'
      : hasItems
        ? `Faltam <span>${formatMoney(remainingForShipping)}</span> para frete por nossa conta`
        : `Frete por nossa conta acima de ${formatMoney(threshold)}`;
  }

  if (cartState.minimumEl) {
    const showMinimumWarning = hasItems && total < minimum;
    cartState.minimumEl.classList.toggle('is-visible', showMinimumWarning);
    cartState.minimumEl.innerHTML = showMinimumWarning
      ? `Faltam <strong>${formatMoney(remainingForMinimum)}</strong> para finalizar — pedido mínimo ${formatMoney(minimum)}.`
      : '';
  }

  if (cartState.checkoutNoteEl) {
    cartState.checkoutNoteEl.hidden = true;
    cartState.checkoutNoteEl.textContent = '';
  }

  if (cartState.footerEl) {
    cartState.footerEl.hidden = !hasItems;
  }

  if (cartState.checkoutBtn) {
    // Atualiza data-attrs que o validador de mínimo lê
    cartState.checkoutBtn.dataset.minCents = String(minimum);
    cartState.checkoutBtn.dataset.cartTotalCents = String(total);
    // Botão só fica fisicamente desabilitado quando o carrinho está vazio
    if (!hasItems) {
      cartState.checkoutBtn.classList.add('is-disabled');
      cartState.checkoutBtn.setAttribute('aria-disabled', 'true');
      cartState.checkoutBtn.disabled = true;
      cartState.checkoutBtn.setAttribute('tabindex', '-1');
    } else {
      cartState.checkoutBtn.classList.remove('is-disabled');
      cartState.checkoutBtn.setAttribute('aria-disabled', 'false');
      cartState.checkoutBtn.disabled = false;
      cartState.checkoutBtn.removeAttribute('tabindex');
    }
  }

  if (!cart.items || cart.items.length === 0) {
    cartState.body.innerHTML = emptyCartHTML();
    return;
  }

  cartState.body.innerHTML = cart.items.map((item) => cartItemHTML(item)).join('');

  initQuantityPickers(cartState.body);

  cartState.body.querySelectorAll('[data-cart-qty-input]').forEach((inp) => {
    inp.addEventListener('change', async () => {
      const key = inp.getAttribute('data-item-key');
      const qty = parseInt(inp.value, 10);
      try {
        const updatedCart = await changeCartItem(key, qty);
        updateCartState(updatedCart);
      } catch {}
    });
  });
}
function updateCountBadges(count) {
  document.querySelectorAll('[data-cart-count]').forEach(el => {
    el.textContent = count > 0 ? count : '';
  });
}

function cartItemHTML(item) {
  const displayQuantity = Math.max(1, parseInt(item.quantity || '1', 10) || 1);
  const img = item.featured_image?.url
    ? `<img src="${item.featured_image.url}&width=144" alt="${escapeHtml(item.title)}" loading="lazy" width="72" height="72">`
    : `<div style="width:72px;height:72px;background:var(--color-bg);border-radius:var(--radius-s);"></div>`;

  const variantLine = (item.variant_title && item.variant_title !== 'Default Title')
    ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>`
    : '';

  return `
    <div class="cart-item" data-item-key="${item.key}">
      <div class="cart-item__image">${img}</div>
      <div>
        <p class="cart-item__title">${escapeHtml(item.title)}</p>
        ${variantLine}
        <div class="cart-item__controls">
          <div class="cart-item__qty">
            <button type="button" aria-label="Diminuir" onclick="(function(btn){
              const inp = btn.closest('.cart-item__qty').querySelector('[data-cart-qty-input]');
              if(inp){ inp.value = Math.max(1, parseInt(inp.value,10)-1); inp.dispatchEvent(new Event('change',{bubbles:true})); }
            })(this)">-</button>
            <span>${displayQuantity}</span>
            <button type="button" aria-label="Aumentar" onclick="(function(btn){
              if(btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
              const inp = btn.closest('.cart-item__qty').querySelector('[data-cart-qty-input]');
              if(inp){ inp.value = parseInt(inp.value,10)+1; inp.dispatchEvent(new Event('change',{bubbles:true})); }
            })(this)">+</button>
            <input type="hidden" data-cart-qty-input data-item-key="${item.key}" value="${displayQuantity}">
          </div>
          <span class="cart-item__price">${formatMoney(item.line_price)}</span>
        </div>
        <button class="cart-item__remove" data-cart-remove="${item.key}" type="button">Remover</button>
      </div>
    </div>`;
}
function emptyCartHTML() {
  return `
    <div class="cart-empty-state">
      <div class="cart-empty-state__icon">&#128722;</div>
      <h3>Seu carrinho ainda está vazio</h3>
      <p>Adicione produtos para continuar sua compra.</p>
      <a class="btn btn--primary" href="/collections/all" onclick="closeCartDrawer()">Explorar produtos</a>
    </div>`;
}
function initSortSelect() {
  const sel = document.querySelector('[data-sort-select]');
  if (!sel) return;
  sel.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sel.value);
    window.location.href = url.toString();
  });
}

async function addToCart(items) {
  const rawPayload = Array.isArray(items) ? items : [items];
  const payload = rawPayload.map(stripInternalPayloadFields);
  const resp = await fetch(window.raminhoTheme.routes.cartAdd, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items: payload })
  });
  if (!resp.ok) {
    let errMsg = 'Não foi possível adicionar o produto.';
    try {
      const data = await resp.json();
      if (data.description) errMsg = data.description;
    } catch {}
    throw new Error(errMsg);
  }
  return fetchCart();
}

async function changeCartItem(key, quantity) {
  const resp = await fetch(window.raminhoTheme.routes.cartChange, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: key, quantity })
  });
  if (!resp.ok) throw new Error('Erro ao atualizar carrinho.');
  return resp.json();
}

async function fetchCart(retries = CART_FETCH_RETRIES) {
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const resp = await fetch(window.raminhoTheme.routes.cartJson, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Accept': 'application/json' }
      });

      if (resp.ok) return resp.json();
      lastError = new Error(`Erro ao buscar carrinho (${resp.status}).`);
    } catch (err) {
      lastError = err;
    }

    if (attempt < retries) await delay(CART_FETCH_RETRY_DELAY_MS * (attempt + 1));
  }

  throw new Error(lastError?.message || 'Erro ao buscar carrinho.');
}

function isKitProductHandle(handle) {
  return String(handle || '').trim().toLowerCase() === RAMINHO_KIT_HANDLE;
}

function tagKitPayloadItems(items, productHandle) {
  const payload = Array.isArray(items) ? items : [items];
  return payload.map((item) => (
    isKitProductHandle(productHandle)
      ? { ...item, _handle: RAMINHO_KIT_HANDLE, quantity: 1 }
      : item
  ));
}

function stripInternalPayloadFields(item) {
  const { _handle, handle, sku, ...publicItem } = item || {};
  return publicItem;
}

function makeEmptyCart() {
  return { item_count: 0, items: [], total_price: 0, items_subtotal_price: 0 };
}

function setButtonLoading(btn, loading) {
  if (!btn) return;
  if (loading) {
    if (!btn.classList.contains('is-success')) {
      btn._originalText = btn.textContent;
    }
    btn.classList.add('is-loading');
    btn.disabled = true;
  } else {
    btn.classList.remove('is-loading');
    btn.disabled = false;
    if (btn._originalText) btn.textContent = btn._originalText;
  }
}

function setButtonSuccess(btn) {
  if (!btn) return;
  btn.classList.remove('is-loading');
  btn.classList.add('is-success');
  btn.textContent = btn.dataset.successLabel || 'Adicionado ao carrinho';
  btn.disabled = false;
  setTimeout(() => {
    btn.classList.remove('is-success');
    if (btn._originalText) btn.textContent = btn._originalText;
  }, 2500);
}

function formatCartItemCount(count) {
  return `${count} ${count === 1 ? 'item' : 'itens'}`;
}
function formatMoney(cents) {
  const format = window.raminhoTheme?.moneyFormat || 'R$ {{amount}}';
  if (!cents && cents !== 0) return '';
  const amount = (cents / 100).toFixed(2).replace('.', ',').replace(/(\d)(?=(\d{3})+,)/g, '$1.');
  return format.replace('{{amount}}', amount)
               .replace('{{amount_no_decimals}}', Math.round(cents / 100))
               .replace('{{amount_with_comma_separator}}', amount);
}

function buildVariantGroups(variants) {
  if (!Array.isArray(variants) || !variants.length) return [];

  const seen = new Set();
  const groups = [];

  variants.forEach((variant, index) => {
    const label = getPrimaryVariantLabel(variant);
    const key = normalizeLabel(label);
    if (seen.has(key)) return;

    const preferredVariant = variants.find((candidate) => normalizeLabel(getPrimaryVariantLabel(candidate)) === key && candidate.available)
      || variants.find((candidate) => normalizeLabel(getPrimaryVariantLabel(candidate)) === key)
      || variant;

    seen.add(key);
    groups.push({
      key,
      label,
      variant: preferredVariant,
      measurement: parseVariantMeasurement(label),
      originalIndex: index
    });
  });

  return groups.sort(compareVariantGroups);
}

function getPrimaryVariantLabel(variant) {
  const rawLabel = variant?.option1 || variant?.public_title || variant?.title || '';
  const label = String(rawLabel).split('/')[0].trim();
  if (!label || /^default title$/i.test(label)) return '1 un';
  return label;
}

function getVariantHelperText(variantGroups) {
  if (!Array.isArray(variantGroups) || variantGroups.length <= 1) return '';

  const label = variantGroups[0]?.label || '';
  const measurement = parseVariantMeasurement(label);
  if (!measurement) return 'Use + e - para ajustar a seleção.';
  if (measurement.kind === 'weight') return 'Use + e - para ajustar o peso.';
  if (measurement.kind === 'volume') return 'Use + e - para ajustar o volume.';
  if (measurement.kind === 'unit') return 'Use + e - para ajustar a quantidade.';
  return 'Use + e - para ajustar a seleção.';
}

function getVariantUnitPriceText(variant, label) {
  if (variant?.unit_price && variant?.unit_price_measurement?.reference_unit) {
    return `${formatMoney(variant.unit_price)} / ${String(variant.unit_price_measurement.reference_unit).toLowerCase()}`;
  }

  const measurement = parseVariantMeasurement(label);
  if (!measurement || measurement.kind !== 'weight') return '';

  const grams = getWeightInGrams(measurement);
  return getUnitPriceTextFromTotalWeight(variant.price, grams);
}

function buildQuantityExpansionConfig(variantGroups) {
  if (!Array.isArray(variantGroups) || variantGroups.length < 2) return null;

  const measuredGroups = variantGroups
    .filter((group) => group?.variant?.available)
    .map((group) => ({
      ...group,
      baseAmount: getMeasurementBaseAmount(group.measurement)
    }))
    .filter((group) => group.measurement && Number.isFinite(group.baseAmount) && group.baseAmount > 0);

  if (measuredGroups.length < 2) return null;

  const maxGroup = measuredGroups[measuredGroups.length - 1];
  const compatibleGroups = measuredGroups.filter((group) => group.measurement.kind === maxGroup.measurement.kind);
  if (compatibleGroups.length < 2) return null;

  const stepGroup = compatibleGroups[compatibleGroups.length - 2];
  const maxBaseAmount = maxGroup.baseAmount;
  const stepBaseAmount = stepGroup.baseAmount;

  if (!Number.isFinite(maxBaseAmount) || !Number.isFinite(stepBaseAmount) || stepBaseAmount <= 0) return null;

  return {
    maxGroup,
    stepGroup,
    kind: maxGroup.measurement.kind,
    maxBaseAmount,
    stepBaseAmount,
    expandableGroups: compatibleGroups,
    compositionCache: new Map()
  };
}

function buildExpandedSelectionState(expansionConfig, extraStepCount, helperText = '') {
  if (!expansionConfig || !Number.isFinite(extraStepCount) || extraStepCount <= 0) return null;

  const totalBaseAmount = expansionConfig.maxBaseAmount + (extraStepCount * expansionConfig.stepBaseAmount);
  const cartItems = buildExpandedCartItems(expansionConfig, totalBaseAmount);
  if (!cartItems.length) return null;

  const composition = summarizeExpandedCartItems(expansionConfig, cartItems);
  const leadGroup = composition.leadGroup || expansionConfig.maxGroup;

  return {
    label: formatExpandedMeasurementLabel(expansionConfig, totalBaseAmount),
    variant: leadGroup?.variant || expansionConfig.maxGroup.variant,
    price: composition.totalPrice,
    compareAtPrice: composition.totalCompareAtPrice,
    unitPriceText: expansionConfig.kind === 'weight'
      ? getUnitPriceTextFromTotalWeight(composition.totalPrice, totalBaseAmount)
      : '',
    helperText,
    cartItems
  };
}

function buildExpandedCartItems(expansionConfig, totalBaseAmount) {
  if (!expansionConfig || !Number.isFinite(totalBaseAmount) || totalBaseAmount <= 0) return [];

  const cached = expansionConfig.compositionCache?.get(totalBaseAmount);
  if (cached) {
    return cached.map((item) => ({ ...item }));
  }

  const groups = [...(expansionConfig.expandableGroups || [])]
    .filter((group) => group?.variant?.id && Number.isFinite(group.baseAmount) && group.baseAmount > 0)
    .sort((a, b) => b.baseAmount - a.baseAmount);

  if (!groups.length) return [];

  const bestByAmount = new Array(totalBaseAmount + 1).fill(null);
  bestByAmount[0] = new Array(groups.length).fill(0);

  for (let amount = 1; amount <= totalBaseAmount; amount += 1) {
    let bestCounts = null;

    groups.forEach((group, index) => {
      const previousAmount = amount - group.baseAmount;
      if (previousAmount < 0) return;

      const previousCounts = bestByAmount[previousAmount];
      if (!previousCounts) return;

      const candidateCounts = previousCounts.slice();
      candidateCounts[index] += 1;

      if (isPreferredCartComposition(candidateCounts, bestCounts)) {
        bestCounts = candidateCounts;
      }
    });

    bestByAmount[amount] = bestCounts;
  }

  const counts = bestByAmount[totalBaseAmount];
  if (!counts) return [];

  const cartItems = counts
    .map((quantity, index) => (
      quantity > 0
        ? { id: groups[index].variant.id, quantity }
        : null
    ))
    .filter(Boolean);

  expansionConfig.compositionCache?.set(
    totalBaseAmount,
    cartItems.map((item) => ({ ...item }))
  );

  return cartItems;
}

function isPreferredCartComposition(candidateCounts, currentCounts) {
  if (!candidateCounts) return false;
  if (!currentCounts) return true;

  const candidateTotal = sumCartComposition(candidateCounts);
  const currentTotal = sumCartComposition(currentCounts);
  if (candidateTotal !== currentTotal) return candidateTotal < currentTotal;

  const candidateLines = countCartCompositionLines(candidateCounts);
  const currentLines = countCartCompositionLines(currentCounts);
  if (candidateLines !== currentLines) return candidateLines < currentLines;

  for (let index = 0; index < candidateCounts.length; index += 1) {
    if (candidateCounts[index] === currentCounts[index]) continue;
    return candidateCounts[index] > currentCounts[index];
  }

  return false;
}

function sumCartComposition(counts) {
  return counts.reduce((total, count) => total + count, 0);
}

function countCartCompositionLines(counts) {
  return counts.reduce((total, count) => total + (count > 0 ? 1 : 0), 0);
}

function summarizeExpandedCartItems(expansionConfig, cartItems) {
  const groupsByVariantId = new Map(
    (expansionConfig.expandableGroups || []).map((group) => [String(group.variant.id), group])
  );

  return cartItems.reduce((summary, item) => {
    const group = groupsByVariantId.get(String(item.id));
    if (!group) return summary;

    summary.totalPrice += (Number(group.variant.price) || 0) * item.quantity;
    summary.totalCompareAtPrice += (Number(group.variant.compare_at_price) || 0) * item.quantity;

    if (!summary.leadGroup || group.baseAmount > summary.leadGroup.baseAmount) {
      summary.leadGroup = group;
    }

    return summary;
  }, {
    totalPrice: 0,
    totalCompareAtPrice: 0,
    leadGroup: null
  });
}

function compareVariantGroups(a, b) {
  const measurementA = a?.measurement;
  const measurementB = b?.measurement;

  if (measurementA && measurementB && measurementA.kind === measurementB.kind) {
    const amountA = getMeasurementBaseAmount(measurementA);
    const amountB = getMeasurementBaseAmount(measurementB);

    if (amountA !== amountB) return amountA - amountB;
  }

  return (a?.originalIndex || 0) - (b?.originalIndex || 0);
}

function getMeasurementBaseAmount(measurement) {
  if (!measurement) return 0;
  if (measurement.kind === 'weight') return getWeightInGrams(measurement);
  if (measurement.kind === 'volume') {
    if (measurement.unit === 'l') return Math.round(measurement.amount * 1000);
    if (measurement.unit === 'ml') return Math.round(measurement.amount);
  }
  if (measurement.kind === 'unit') return Math.round(measurement.amount);
  return 0;
}

function formatExpandedMeasurementLabel(expansionConfig, totalBaseAmount) {
  if (!expansionConfig || !Number.isFinite(totalBaseAmount) || totalBaseAmount <= 0) return '';
  if (expansionConfig.kind === 'weight') return formatWeightLabelFromGrams(totalBaseAmount);
  if (expansionConfig.kind === 'unit') return `${Math.round(totalBaseAmount)} un`;
  if (expansionConfig.kind === 'volume') return formatVolumeLabel(totalBaseAmount);
  return expansionConfig.maxGroup?.label || '';
}

function getWeightInGrams(measurement) {
  if (!measurement || measurement.kind !== 'weight') return 0;
  if (measurement.unit === 'kg') return Math.round(measurement.amount * 1000);
  if (measurement.unit === 'g') return Math.round(measurement.amount);
  return 0;
}

function getUnitPriceTextFromTotalWeight(totalCents, grams) {
  if (!Number.isFinite(totalCents) || totalCents <= 0) return '';
  if (!Number.isFinite(grams) || grams <= 0) return '';

  const perKgCents = Math.round((totalCents * 1000) / grams);
  if (!Number.isFinite(perKgCents) || perKgCents <= 0) return '';
  return `${formatMoney(perKgCents)} / kg`;
}

function formatWeightLabelFromGrams(grams) {
  if (!Number.isFinite(grams) || grams <= 0) return '';

  if (grams >= 1000) {
    const kilos = grams / 1000;
    const formatted = Number.isInteger(kilos)
      ? String(kilos)
      : kilos.toFixed(2).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
    return `${formatted} Kg`;
  }

  return `${Math.round(grams)} g`;
}

function formatVolumeLabel(totalMilliliters) {
  if (!Number.isFinite(totalMilliliters) || totalMilliliters <= 0) return '';

  if (totalMilliliters >= 1000) {
    const liters = totalMilliliters / 1000;
    const formatted = Number.isInteger(liters)
      ? String(liters)
      : liters.toFixed(2).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
    return `${formatted} L`;
  }

  return `${Math.round(totalMilliliters)} ml`;
}

function parseVariantMeasurement(label) {
  if (!label) return null;

  const match = String(label).trim().toLowerCase().match(/(\d+(?:[.,]\d+)?)\s*(kg|g|un|und|unidade|ml|l)\b/);
  if (!match) return null;

  const amount = Number.parseFloat(match[1].replace(',', '.'));
  const unit = match[2];
  if (!Number.isFinite(amount) || amount <= 0) return null;

  if (unit === 'kg' || unit === 'g') return { kind: 'weight', amount, unit };
  if (unit === 'ml' || unit === 'l') return { kind: 'volume', amount, unit };
  return { kind: 'unit', amount, unit };
}

function normalizeLabel(value) {
  return String(value || '').trim().toLowerCase();
}
function parseJson(el) {
  if (!el) return null;
  try { return JSON.parse(el.textContent); } catch { return null; }
}

function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  return str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    parseVariantMeasurement,
    buildVariantGroups,
    getPrimaryVariantLabel,
    getVariantHelperText,
    getVariantUnitPriceText,
    buildQuantityExpansionConfig,
    buildExpandedCartItems,
    buildExpandedSelectionState,
    getMeasurementBaseAmount,
    getWeightInGrams,
    getUnitPriceTextFromTotalWeight,
    formatWeightLabelFromGrams,
    formatVolumeLabel,
    formatExpandedMeasurementLabel,
    compareVariantGroups,
    normalizeLabel,
    formatMoney,
    escapeHtml,
  };
}

/* ============================================================
 * Validação de pedido mínimo + modal de "Faltam R$X"
 * Camada 2 da defesa em profundidade (Liquid renderiza,
 * JS valida no clique, Yampi valida no servidor).
 * ============================================================ */

const RAMINHO_MIN_FALLBACK = 9990; // R$99,90 em centavos

function validateMinimumOrShowModal(button) {
  if (!(button instanceof HTMLElement)) return true;

  const minCents = parseInt(
    button.dataset.minCents
      || (window.raminhoTheme && window.raminhoTheme.cartRules && window.raminhoTheme.cartRules.minimumCents)
      || RAMINHO_MIN_FALLBACK,
    10
  );
  const totalCents = parseInt(button.dataset.cartTotalCents || '0', 10);

  if (totalCents >= minCents) return true;

  showMinimumModal(minCents - totalCents, minCents);
  return false;
}

let raminhoMinimumModalEl = null;

function showMinimumModal(faltamCents, minCents) {
  ensureMinimumModal();
  const faltam = formatMoney(faltamCents);
  const minimo = formatMoney(minCents);

  const msgEl = raminhoMinimumModalEl.querySelector('[data-min-modal-message]');
  if (msgEl) {
    msgEl.innerHTML = `Faltam <strong>${faltam}</strong> para você finalizar.<br><span class="min-modal__sub">Pedido mínimo da Raminho: ${minimo}</span>`;
  }

  const suggestionsEl = raminhoMinimumModalEl.querySelector('[data-min-modal-suggestions]');
  if (suggestionsEl && suggestionsEl.dataset.loaded !== 'true') {
    loadMinimumSuggestions(suggestionsEl, faltamCents);
  }

  raminhoMinimumModalEl.classList.add('is-open');
  raminhoMinimumModalEl.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  const overlay = document.querySelector('[data-overlay]');
  if (overlay) overlay.classList.add('is-active');
}

function closeMinimumModal() {
  if (!raminhoMinimumModalEl) return;
  raminhoMinimumModalEl.classList.remove('is-open');
  raminhoMinimumModalEl.setAttribute('aria-hidden', 'true');
  closeCartDrawer();
}

function ensureMinimumModal() {
  if (raminhoMinimumModalEl) return;

  raminhoMinimumModalEl = document.createElement('div');
  raminhoMinimumModalEl.className = 'minimum-modal';
  raminhoMinimumModalEl.setAttribute('data-minimum-modal', '');
  raminhoMinimumModalEl.setAttribute('role', 'dialog');
  raminhoMinimumModalEl.setAttribute('aria-modal', 'true');
  raminhoMinimumModalEl.setAttribute('aria-hidden', 'true');
  raminhoMinimumModalEl.setAttribute('aria-labelledby', 'minimum-modal-title');

  raminhoMinimumModalEl.innerHTML = `
    <div class="minimum-modal__content" role="document">
      <button type="button" class="minimum-modal__close" data-min-modal-close aria-label="Fechar">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
          <path d="M1 1l16 16M17 1L1 17"/>
        </svg>
      </button>
      <div class="minimum-modal__icon" aria-hidden="true">🍐</div>
      <h2 class="minimum-modal__title" id="minimum-modal-title">Quase lá!</h2>
      <p class="minimum-modal__message" data-min-modal-message></p>
      <div class="minimum-modal__suggestions-wrap">
        <p class="minimum-modal__suggestions-label">Sugestões pra completar o pedido</p>
        <div class="minimum-modal__suggestions" data-min-modal-suggestions>
          <p class="minimum-modal__loading">Carregando sugestões…</p>
        </div>
      </div>
      <div class="minimum-modal__actions">
        <button type="button" class="btn btn--purchase btn--wide" data-min-modal-close>Continuar comprando</button>
      </div>
    </div>
  `;

  document.body.appendChild(raminhoMinimumModalEl);

  // Fecha ao clicar no X, no botão ou fora do conteúdo
  raminhoMinimumModalEl.addEventListener('click', (e) => {
    if (e.target.closest('[data-min-modal-close]') || e.target === raminhoMinimumModalEl) {
      closeMinimumModal();
    }
  });

  // ESC fecha
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && raminhoMinimumModalEl?.classList.contains('is-open')) {
      closeMinimumModal();
    }
  });
}

async function loadMinimumSuggestions(container, faltamCents) {
  try {
    // Busca produtos da coleção 'all' até R$50 que possam completar o pedido
    const targetMaxCents = Math.max(faltamCents + 1500, 5000); // até faltam+R$15 ou pelo menos R$50
    const res = await fetch('/products.json?limit=20', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('fetch failed');
    const data = await res.json();
    const products = (data.products || [])
      .map((p) => {
        const variant = p.variants?.[0];
        if (!variant) return null;
        const priceCents = Math.round(parseFloat(variant.price) * 100);
        return {
          title: p.title,
          handle: p.handle,
          priceCents,
          variantId: variant.id,
          image: p.images?.[0]?.src || null
        };
      })
      .filter((p) => p && p.priceCents > 0 && p.priceCents <= targetMaxCents)
      .sort((a, b) => Math.abs(a.priceCents - faltamCents) - Math.abs(b.priceCents - faltamCents))
      .slice(0, 4);

    if (products.length === 0) {
      container.innerHTML = '<p class="minimum-modal__empty">Volte ao catálogo e adicione mais produtos.</p>';
      container.dataset.loaded = 'true';
      return;
    }

    container.innerHTML = products.map((p) => `
      <div class="minimum-modal__sug-item">
        ${p.image ? `<img src="${p.image}" alt="${escapeHtml(p.title)}" loading="lazy">` : '<div class="minimum-modal__sug-placeholder"></div>'}
        <div class="minimum-modal__sug-info">
          <span class="minimum-modal__sug-title">${escapeHtml(p.title)}</span>
          <span class="minimum-modal__sug-price">${formatMoney(p.priceCents)}</span>
        </div>
        <button type="button" class="minimum-modal__sug-add" data-add-variant="${p.variantId}">+ Adicionar</button>
      </div>
    `).join('');

    container.dataset.loaded = 'true';

    // Wire add-to-cart
    container.querySelectorAll('[data-add-variant]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.addVariant;
        btn.disabled = true;
        btn.textContent = 'Adicionando…';
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });

          btn.textContent = '✓ Adicionado';

          // Busca o carrinho atualizado
          const cart = await fetchCart();
          const minCents = parseInt(
            window.raminhoTheme?.cartRules?.minimumCents || String(RAMINHO_MIN_FALLBACK), 10
          );

          // Atualiza o drawer com o novo estado do carrinho
          updateCartState(cart);
          renderCartDrawer(cart);

          if (cart.total_price >= minCents) {
            // Pedido completou o mínimo: fecha modal e abre carrinho
            closeMinimumModal();
            openCartDrawer();
          } else {
            // Ainda abaixo do mínimo: atualiza mensagem e recarrega sugestões
            const novoFaltam = minCents - cart.total_price;
            const msgEl = raminhoMinimumModalEl?.querySelector('[data-min-modal-message]');
            if (msgEl) {
              msgEl.innerHTML = `Adicione mais <strong>${formatMoney(novoFaltam)}</strong> para finalizar seu pedido (mínimo ${formatMoney(minCents)}).`;
            }
            const sugEl = raminhoMinimumModalEl?.querySelector('[data-min-modal-suggestions]');
            if (sugEl) {
              sugEl.dataset.loaded = 'false';
              sugEl.innerHTML = '<p class="minimum-modal__loading">Atualizando sugestões…</p>';
              loadMinimumSuggestions(sugEl, novoFaltam);
            }
          }
        } catch (err) {
          btn.disabled = false;
          btn.textContent = '+ Adicionar';
        }
      });
    });
  } catch (err) {
    container.innerHTML = '<p class="minimum-modal__empty">Volte ao catálogo e adicione mais produtos.</p>';
    container.dataset.loaded = 'true';
  }
}

/* ─── PRODUCT STICKY ADD-TO-CART BAR (mobile only) ────────────────────── */
function initProductStickyBar() {
  const stickyBar = document.querySelector('[data-product-sticky-bar]');
  const originalBtn = document.querySelector('[data-product-section] [data-add-to-cart]');
  const stickyBtn = stickyBar?.querySelector('[data-sticky-add-to-cart]');
  const productForm = document.querySelector('[data-product-section] [data-product-form]');
  if (!stickyBar || !originalBtn || !stickyBtn || !productForm) return;

  // Click no botão sticky → submete o form do produto (mesmo fluxo do botão original)
  stickyBtn.addEventListener('click', (e) => {
    e.preventDefault();
    if (stickyBtn.disabled) return;
    // Dispara um submit programático no form para reusar toda a lógica existente
    if (typeof productForm.requestSubmit === 'function') {
      productForm.requestSubmit();
    } else {
      productForm.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  });

  // Mantém o estado do botão sticky em sincronia com o original
  const syncStickyState = () => {
    if (originalBtn.disabled || originalBtn.matches(':disabled')) {
      stickyBtn.disabled = true;
      stickyBtn.textContent = 'Indisponível';
    } else {
      stickyBtn.disabled = false;
      stickyBtn.textContent = 'Adicionar';
    }
    // Atualiza preço sticky se mudou
    const priceEl = document.querySelector('[data-product-price]');
    const stickyPrice = stickyBar.querySelector('[data-sticky-price]');
    if (priceEl && stickyPrice) {
      stickyPrice.textContent = priceEl.textContent.trim();
    }
  };

  new MutationObserver(syncStickyState).observe(originalBtn, {
    attributes: true,
    attributeFilter: ['disabled', 'class']
  });
  const priceEl = document.querySelector('[data-product-price]');
  if (priceEl) {
    new MutationObserver(syncStickyState).observe(priceEl, { childList: true, characterData: true, subtree: true });
  }

  // Mostra/esconde com base na visibilidade do botão original
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Quando o botão original NÃO está visível, mostra o sticky
        if (entry.isIntersecting) {
          stickyBar.classList.remove('is-visible');
          stickyBar.setAttribute('aria-hidden', 'true');
        } else {
          stickyBar.classList.add('is-visible');
          stickyBar.setAttribute('aria-hidden', 'false');
        }
      });
    }, { rootMargin: '0px 0px -100px 0px', threshold: 0 });
    observer.observe(originalBtn);
  }

  // Esconde o sticky bar quando o drawer do carrinho está aberto
  // (drawer ocupa o bottom da tela no mobile e visualmente bate em cima do sticky)
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  if (cartDrawer && 'MutationObserver' in window) {
    new MutationObserver(() => {
      if (cartDrawer.classList.contains('is-open')) {
        stickyBar.classList.add('is-hidden-by-drawer');
      } else {
        stickyBar.classList.remove('is-hidden-by-drawer');
      }
    }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }
}

/* ─── GALLERY LIGHTBOX ────────────────────────────────────────────────── */
let raminhoLightbox = null;
let raminhoLightboxState = { images: [], index: 0 };

function initGalleryLightbox(root) {
  root.querySelectorAll('[data-gallery]').forEach((gallery) => {
    if (gallery.dataset.lightboxReady === 'true') return;
    gallery.dataset.lightboxReady = 'true';

    const mainFrame = gallery.querySelector('.gallery-main__frame');
    if (!mainFrame) return;

    // Coleta apenas imagens (vídeos tocam inline, não vão pro lightbox)
    const collectImages = () => {
      const imgs = [];
      mainFrame.querySelectorAll('.gallery-main__media[data-media-type="image"] img').forEach((img) => {
        imgs.push({ src: img.currentSrc || img.src, alt: img.alt || '' });
      });
      return imgs;
    };

    // Índice da imagem atualmente ativa (entre as imagens, ignorando vídeos)
    const currentImageIndex = () => {
      const activeMedia = mainFrame.querySelector('.gallery-main__media.is-active');
      if (!activeMedia || activeMedia.dataset.mediaType !== 'image') return 0;
      const allImageMedia = [...mainFrame.querySelectorAll('.gallery-main__media[data-media-type="image"]')];
      return Math.max(0, allImageMedia.indexOf(activeMedia));
    };

    mainFrame.addEventListener('click', (e) => {
      // Não abre lightbox se o clique foi em vídeo, iframe ou model
      if (e.target.closest('video, iframe, model-viewer, .gallery-main__video')) return;

      // Não abre lightbox se a mídia ativa não for imagem
      const activeMedia = mainFrame.querySelector('.gallery-main__media.is-active');
      if (!activeMedia || activeMedia.dataset.mediaType !== 'image') return;

      const imgs = collectImages();
      if (!imgs.length) return;
      e.preventDefault();
      openLightbox(imgs, currentImageIndex());
    });
  });
}

function ensureLightbox() {
  if (raminhoLightbox) return raminhoLightbox;

  raminhoLightbox = document.createElement('div');
  raminhoLightbox.className = 'gallery-lightbox';
  raminhoLightbox.setAttribute('role', 'dialog');
  raminhoLightbox.setAttribute('aria-modal', 'true');
  raminhoLightbox.setAttribute('aria-hidden', 'true');
  raminhoLightbox.innerHTML = `
    <div class="gallery-lightbox__inner">
      <button type="button" class="gallery-lightbox__close" data-lightbox-close aria-label="Fechar">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 1l16 16M17 1L1 17"/></svg>
      </button>
      <button type="button" class="gallery-lightbox__nav gallery-lightbox__nav--prev" data-lightbox-prev aria-label="Imagem anterior">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <img class="gallery-lightbox__img" data-lightbox-img alt="">
      <button type="button" class="gallery-lightbox__nav gallery-lightbox__nav--next" data-lightbox-next aria-label="Próxima imagem">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
      <div class="gallery-lightbox__counter" data-lightbox-counter></div>
    </div>
  `;
  document.body.appendChild(raminhoLightbox);

  // Eventos
  raminhoLightbox.addEventListener('click', (e) => {
    if (e.target.closest('[data-lightbox-close]') || e.target === raminhoLightbox) {
      closeLightbox();
    } else if (e.target.closest('[data-lightbox-prev]')) {
      navigateLightbox(-1);
    } else if (e.target.closest('[data-lightbox-next]')) {
      navigateLightbox(1);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!raminhoLightbox?.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeLightbox();
    else if (e.key === 'ArrowLeft') navigateLightbox(-1);
    else if (e.key === 'ArrowRight') navigateLightbox(1);
  });

  return raminhoLightbox;
}

function openLightbox(images, startIndex = 0) {
  ensureLightbox();
  raminhoLightboxState.images = images;
  raminhoLightboxState.index = startIndex;
  renderLightboxImage();
  raminhoLightbox.classList.add('is-open');
  raminhoLightbox.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';

  // Esconde botões de nav se só tem 1 imagem
  const navs = raminhoLightbox.querySelectorAll('.gallery-lightbox__nav');
  navs.forEach((n) => { n.style.display = images.length > 1 ? 'flex' : 'none'; });
  const counter = raminhoLightbox.querySelector('[data-lightbox-counter]');
  if (counter) counter.style.display = images.length > 1 ? 'block' : 'none';
}

function closeLightbox() {
  if (!raminhoLightbox) return;
  raminhoLightbox.classList.remove('is-open');
  raminhoLightbox.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}

function navigateLightbox(direction) {
  const total = raminhoLightboxState.images.length;
  if (total <= 1) return;
  raminhoLightboxState.index = (raminhoLightboxState.index + direction + total) % total;
  renderLightboxImage();
}

function renderLightboxImage() {
  const img = raminhoLightbox?.querySelector('[data-lightbox-img]');
  const counter = raminhoLightbox?.querySelector('[data-lightbox-counter]');
  const current = raminhoLightboxState.images[raminhoLightboxState.index];
  if (img && current) {
    img.src = current.src;
    img.alt = current.alt;
  }
  if (counter) {
    counter.textContent = `${raminhoLightboxState.index + 1} / ${raminhoLightboxState.images.length}`;
  }
}

/* ─── GALLERY MOBILE SWIPE ────────────────────────────────────────────── */
function initGallerySwipe(root) {
  root.querySelectorAll('[data-gallery]').forEach((gallery) => {
    if (gallery.dataset.swipeReady === 'true') return;
    gallery.dataset.swipeReady = 'true';

    const main = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    if (!main || thumbs.length < 2) return;

    let startX = 0, startY = 0, currentX = 0, isSwiping = false;
    const SWIPE_THRESHOLD = 50;

    main.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isSwiping = true;
    }, { passive: true });

    main.addEventListener('touchmove', (e) => {
      if (!isSwiping || e.touches.length !== 1) return;
      currentX = e.touches[0].clientX;
      const deltaX = currentX - startX;
      const deltaY = Math.abs(e.touches[0].clientY - startY);
      // Cancela se for scroll vertical
      if (deltaY > Math.abs(deltaX)) {
        isSwiping = false;
      }
    }, { passive: true });

    main.addEventListener('touchend', () => {
      if (!isSwiping) return;
      const deltaX = currentX - startX;
      if (Math.abs(deltaX) >= SWIPE_THRESHOLD) {
        const activeIdx = [...thumbs].findIndex((t) => t.classList.contains('is-active'));
        const nextIdx = deltaX < 0
          ? Math.min(thumbs.length - 1, activeIdx + 1)
          : Math.max(0, activeIdx - 1);
        if (nextIdx !== activeIdx && thumbs[nextIdx]) {
          thumbs[nextIdx].click();
        }
      }
      isSwiping = false;
      startX = currentX = 0;
    });
  });
}

/* ─── CART ITEM REMOVE (button-based) ─────────────────────────────────── */
function initCartItemRemove() {
  document.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-cart-item-remove]');
    if (!removeBtn) return;
    e.preventDefault();
    if (removeBtn.disabled) return;

    const itemKey = removeBtn.dataset.itemKey;
    if (!itemKey) return;

    removeBtn.disabled = true;
    removeBtn.textContent = 'Removendo…';

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: itemKey, quantity: 0 })
      });
      window.location.reload();
    } catch (err) {
      removeBtn.disabled = false;
      removeBtn.textContent = 'Remover';
    }
  });
}

/* ─── SEARCH FORM VALIDATION ──────────────────────────────────────────── */
function initSearchValidation(root) {
  root.querySelectorAll('[data-search-form]').forEach((form) => {
    if (form.dataset.searchReady === 'true') return;
    form.dataset.searchReady = 'true';

    const input = form.querySelector('[data-search-input]');
    if (!input) return;

    form.addEventListener('submit', (e) => {
      const value = (input.value || '').trim();
      if (value.length < 2) {
        e.preventDefault();
        input.focus();
        // Feedback visual sutil
        input.style.boxShadow = '0 0 0 2px rgba(192,57,43,0.4)';
        setTimeout(() => { input.style.boxShadow = ''; }, 1500);
      }
    });
  });
}

/* ─── ANNOUNCEMENT BAR ROTATOR ────────────────────────────────────────── */
function initAnnouncementRotator() {
  const rotator = document.querySelector('[data-announcement-rotator]');
  if (!rotator) return;

  const messages = [...rotator.querySelectorAll('.announcement-bar__msg')];
  if (messages.length < 2) return;

  let currentIdx = Math.max(0, messages.findIndex((m) => m.classList.contains('is-active')));
  const ROTATE_INTERVAL = 5000;

  // Estado-limpo: garante que SÓ a mensagem ativa fica visível.
  // (auto-corrige qualquer bagunça de frames perdidos em aba de fundo)
  const normalizar = () => {
    messages.forEach((m, i) => {
      m.classList.remove('is-entering', 'is-leaving');
      m.classList.toggle('is-active', i === currentIdx);
    });
  };

  const showNext = () => {
    if (document.hidden) return; // não rotaciona em aba escondida
    const current = messages[currentIdx];
    const nextIdx = (currentIdx + 1) % messages.length;
    const next = messages[nextIdx];

    messages.forEach((m) => {
      if (m !== current && m !== next) m.classList.remove('is-active', 'is-entering', 'is-leaving');
    });

    current.classList.remove('is-active');
    current.classList.add('is-leaving');
    next.classList.add('is-entering');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        next.classList.remove('is-entering');
        next.classList.add('is-active');
      });
    });

    setTimeout(() => {
      current.classList.remove('is-leaving');
    }, 700);

    currentIdx = nextIdx;
  };

  let intervalId = setInterval(showNext, ROTATE_INTERVAL);
  const reiniciar = () => {
    clearInterval(intervalId);
    intervalId = setInterval(showNext, ROTATE_INTERVAL);
  };

  // Pausa rotação se o usuário interage com a barra (acessibilidade)
  rotator.addEventListener('mouseenter', () => clearInterval(intervalId));
  rotator.addEventListener('mouseleave', reiniciar);

  // Aba volta ao primeiro plano: normaliza o estado e recomeça do zero
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearInterval(intervalId);
    } else {
      normalizar();
      reiniciar();
    }
  });
}

/* ─── CHECKOUT CROSS-SELL MODAL (aparece ao clicar Finalizar) ─────────── */
let raminhoCrosssellModalEl = null;

function getBestsellersData() {
  // Lê os best-sellers que o Liquid renderiza no cart-drawer.liquid
  const script = document.querySelector('[data-bestsellers]');
  if (!script) return [];
  try {
    const data = JSON.parse(script.textContent);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

async function showCrosssellModalAndProceed(callback) {
  // Se a flag de já visto está setada, vai direto pro checkout
  if (sessionStorage.getItem('raminhoCrosssellShown') === 'true') {
    callback();
    return;
  }

  // Carrega best-sellers (renderizados via Liquid, ordem definida pelo Shopify Admin)
  const bestsellers = getBestsellersData();
  if (!bestsellers.length) {
    callback();
    return;
  }

  // Pega items do carrinho atual via fetchCart
  let cartItems = [];
  try {
    const cart = await fetchCart();
    cartItems = cart.items || [];
  } catch {}
  const cartProductIds = new Set(cartItems.map((i) => i.product_id));

  // Filtra best-sellers que já estão no carrinho (mantém ordem original do Liquid)
  const eligible = bestsellers.filter((p) => !cartProductIds.has(p.id));

  if (!eligible.length) {
    callback();
    return;
  }

  // Pega os 3 PRIMEIROS (já ordenados por best-selling do Liquid)
  // Se quiser variar, sorteia 3 entre os top 8 — ainda prioriza mais vendidos sem repetir sempre os mesmos 3
  const topPool = eligible.slice(0, 8);
  const products = [...topPool].sort(() => Math.random() - 0.5).slice(0, 3);

  // Marca como visto nesta sessão (não vai aparecer de novo até nova aba)
  sessionStorage.setItem('raminhoCrosssellShown', 'true');

  // Abre o modal
  ensureCrosssellModal();
  renderCrosssellProducts(products);
  raminhoCrosssellModalEl._proceedCallback = callback;
  raminhoCrosssellModalEl.classList.add('is-open');
  raminhoCrosssellModalEl.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
}

function ensureCrosssellModal() {
  if (raminhoCrosssellModalEl) return raminhoCrosssellModalEl;

  raminhoCrosssellModalEl = document.createElement('div');
  raminhoCrosssellModalEl.className = 'crosssell-modal';
  raminhoCrosssellModalEl.setAttribute('role', 'dialog');
  raminhoCrosssellModalEl.setAttribute('aria-modal', 'true');
  raminhoCrosssellModalEl.setAttribute('aria-hidden', 'true');
  raminhoCrosssellModalEl.setAttribute('aria-labelledby', 'crosssell-modal-title');

  raminhoCrosssellModalEl.innerHTML = `
    <div class="crosssell-modal__content" role="document">
      <button type="button" class="crosssell-modal__close" data-crosssell-close aria-label="Fechar">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
          <path d="M1 1l16 16M17 1L1 17"/>
        </svg>
      </button>
      <div class="crosssell-modal__icon" aria-hidden="true">🍎</div>
      <h2 class="crosssell-modal__title" id="crosssell-modal-title">Antes de finalizar…</h2>
      <p class="crosssell-modal__sub">Que tal levar uma dessas frutas também?</p>
      <div class="crosssell-modal__items" data-crosssell-items></div>
      <div class="crosssell-modal__actions">
        <button type="button" class="btn btn--purchase btn--wide" data-crosssell-proceed>
          Continuar para o pagamento
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(raminhoCrosssellModalEl);

  // Eventos
  raminhoCrosssellModalEl.addEventListener('click', (e) => {
    if (e.target.closest('[data-crosssell-close]') || e.target === raminhoCrosssellModalEl) {
      closeCrosssellModal();
    } else if (e.target.closest('[data-crosssell-proceed]')) {
      const cb = raminhoCrosssellModalEl._proceedCallback;
      closeCrosssellModal();
      if (typeof cb === 'function') cb();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && raminhoCrosssellModalEl?.classList.contains('is-open')) {
      closeCrosssellModal();
    }
  });

  return raminhoCrosssellModalEl;
}

function closeCrosssellModal() {
  if (!raminhoCrosssellModalEl) return;
  raminhoCrosssellModalEl.classList.remove('is-open');
  raminhoCrosssellModalEl.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
  raminhoCrosssellModalEl._proceedCallback = null;
}

function renderCrosssellProducts(products) {
  const itemsEl = raminhoCrosssellModalEl?.querySelector('[data-crosssell-items]');
  if (!itemsEl) return;

  itemsEl.innerHTML = products.map((p) => {
    const img = p.image
      ? `<img src="${p.image}" alt="${escapeHtml(p.title)}" loading="lazy">`
      : '<div class="crosssell-modal__sug-placeholder"></div>';
    return `
      <div class="crosssell-modal__item">
        ${img}
        <div class="crosssell-modal__item-info">
          <span class="crosssell-modal__item-title">${escapeHtml(p.title)}</span>
          <span class="crosssell-modal__item-price">${formatMoney(p.price_cents)}</span>
        </div>
        <button type="button" class="crosssell-modal__item-add" data-crosssell-add="${p.variant_id}">+ Adicionar</button>
      </div>
    `;
  }).join('');

  // Wire add buttons
  itemsEl.querySelectorAll('[data-crosssell-add]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const variantId = btn.dataset.crosssellAdd;
      btn.disabled = true;
      btn.textContent = 'Adicionando…';
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        btn.textContent = '✓ Adicionado';
        // Atualiza estado do carrinho silenciosamente
        const cart = await fetchCart();
        updateCartState(cart);
        renderCartDrawer(cart);
      } catch (err) {
        btn.disabled = false;
        btn.textContent = '+ Adicionar';
      }
    });
  });
}
